import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface MemberList {
    fields: {
        textarea: string;
    };
}

const enum fields {
    text = "text",
    divisions = "divisions",
    fullWidthButton = "fullWidthButton",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        members: any;
        [fields.divisions]: MemberList[];
        [fields.fullWidthButton]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    checkIconStyles?: IconPresentationProps;
    guestCheckOutGrid?: GridItemProps;
    checkoutWrapper?: InjectableCss;
    textAreaStyles?: InjectableCss;
    createAccountBtn?: ButtonPresentationProps;
    createAccountBtnWrapper?: InjectableCss;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    signInCreateNewAccountGridContainer: {
        gap: 20,
        css: css`
            border-left: 2px solid ${COLORS.background.grayShade};
            height: 100%;
            padding-left: 64px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border-top: 2px solid ${COLORS.background.grayShade};
                        border-left: none;
                        padding-left: 0px;
                        padding-top: 25px;
                        padding-bottom: 25px;
                    `,
                    css`
                        border-top: 2px solid ${COLORS.background.grayShade};
                        border-left: none;
                        padding-left: 0px;
                        padding-top: 25px;
                        padding-bottom: 25px;
                    `,
                    css`
                        border-top: 2px solid ${COLORS.background.grayShade};
                        border-left: none;
                        padding-left: 0px;
                        padding-top: 25px;
                        padding-bottom: 25px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    createAccountBtnWrapper: {
        css: css`
            padding: 15px;
        `,
    },
    createAccountBtn: {
        variant: "primary",
        css: css`
            padding: 7px 36px;
            height: 32px;
            min-width: 93px;
            min-height: 41px;
            border-radius: 24px;
        `,
    },
    checkIconStyles: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    guestCheckOutGrid: {
        width: 12,
        css: css`
            flex-direction: column;
            padding: 0px;
        `,
    },
    checkoutWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: 16px;
        `,
    },
    textAreaStyles: {
        css: css`
            margin-left: 6px;
        `,
    },
    signInCreateNewAccountTitle: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
            font-size: ${TYPOGRAPHY.size.xxlarge} !important;
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold} !important;
            line-height: 40px !important;
        `,
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
        css: css`
            padding: 9px 15px 9px 15px;
        `,
    },
    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
            padding: 0 0 15px 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: {
        css: css`
            background-color: transparent;
            border-radius: 24px;
            margin-top: 8px;
            color: ${getColor("secondary")};
            &:hover {
                background-color: transparent;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 1rem;
                            margin-top: 8px;
                        `,
                        null,
                        css`
                            margin-right: 1rem;
                        `,
                        css`
                            margin-bottom: 1rem;
                            margin-top: 8px;
                            margin-left: 0px;
                        `,
                        css`
                            margin-right: 1rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    createNewAccountButton: {
        variant: "primary",
        css: css`
            border-radius: 24px !important;
            margin: 24px 0px 0px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 8px;
                        `,
                        css`
                            margin-bottom: 8px;
                        `,
                        css`
                            margin-bottom: 8px;
                        `,
                        css`
                            margin-bottom: 8px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
};

export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const createButtonCss = fields.fullWidthButton
        ? css`
              width: 100%;
          `
        : css`
              margin-top: 24px;
              border-radius: 24px;
          `;

    return (
        <GridContainer {...styles.signInCreateNewAccountGridContainer}>
            <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create New Account")}</Typography>
            </GridItem>
            <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                <Typography {...styles.signInCreateNewAccountText}>{fields.text}</Typography>
            </GridItem>
            <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                <>
                    <GridItem {...styles.guestCheckOutGrid}>
                        {fields.divisions?.map(division => (
                            // eslint-disable-next-line react/jsx-key
                            <StyledWrapper {...styles.checkoutWrapper}>
                                <Icon src={Check} {...styles.checkIconStyles} />
                                <StyledWrapper {...styles.textAreaStyles}>{division.fields.textarea}</StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </GridItem>
                </>

                {allowGuestCheckout ? (
                    <>
                        <Button
                            css={createButtonCss}
                            variant="primary"
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create an Account")}
                        </Button>
                        <Button
                            {...styles.checkoutAsGuestButton}
                            onClick={handleCheckoutAsGuest}
                            data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                        >
                            {translate("Checkout as Guest")}
                        </Button>
                    </>
                ) : (
                    <>
                        <StyledWrapper {...styles.createAccountBtnWrapper}>
                            <Button
                                onClick={handleCreateNewAccount}
                                data-test-selector="signInCreateNewAccount_createNewAccount"
                                {...styles.createAccountBtn}
                                variant="primary"
                            >
                                {translate("Create Account")}
                            </Button>
                        </StyledWrapper>
                    </>
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",

        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.textarea;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "textarea",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
